import {
  SETTINGS_TAB_SEARCH_PARAM,
  parseSettingsTabFromSearch,
} from 'features/settings_modal/constants'

/** Base path for profile settings routes (general + login). */
export const PROFILE_SETTINGS_BASE = '/app/personal/profile'

/** Base path for personal security / privacy settings. */
export const PERSONAL_SECURITY_BASE = '/app/personal/security'

/**
 * Build a monolith-safe browser URL for a personal settings tab (base path + `settings_tab` query).
 * Omits the query param for the default/general tab.
 *
 * @param {string} basePath
 * @param {string | null | undefined} tabSlug
 * @param {string} [existingSearch]
 * @param {string} [hash]
 * @returns {{ pathname: string, search: string, hash: string }}
 */
export function personalBrowserTabTarget(basePath, tabSlug, existingSearch = '', hash = '') {
  const params = new URLSearchParams(existingSearch)
  if (tabSlug == null || tabSlug === '' || tabSlug === 'general') {
    params.delete(SETTINGS_TAB_SEARCH_PARAM)
  } else {
    params.set(SETTINGS_TAB_SEARCH_PARAM, tabSlug)
  }
  const qs = params.toString()
  return {
    pathname: basePath,
    search: qs ? `?${qs}` : '',
    hash,
  }
}

/**
 * Tab selection for legacy full-page headers that use base path + `settings_tab` (not nested routes).
 *
 * @param {{ pathname: string, search: string, tabs: Array<{ route: string | { pathname: string, search?: string } }> }} args
 * @returns {Array<{ route: string | object, selected: boolean, key: string, title: string }>}
 */
export function generateBrowserTabs({ pathname, search, tabs }) {
  const activeTabSlug = parseSettingsTabFromSearch(search)

  const selectedTab = tabs.find((tab) => {
    const route = tab.route
    if (typeof route === 'string') {
      return pathname === route && (activeTabSlug == null || activeTabSlug === 'general')
    }
    const routeTabSlug = parseSettingsTabFromSearch(route.search ?? '')
    return pathname === route.pathname && activeTabSlug === routeTabSlug
  }) ?? tabs[0]

  return tabs.map((tab) => {
    const route = tab.route
    const key = typeof route === 'string'
      ? route
      : `${route.pathname}${route.search ?? ''}`

    return {
      ...tab,
      key,
      selected: tab === selectedTab,
    }
  })
}

const PROFILE_LEGACY_NESTED_SLUGS = Object.freeze({
  general: 'general',
  login: 'login',
})

const SECURITY_LEGACY_NESTED_PREFIX = `${PERSONAL_SECURITY_BASE}/`

/**
 * When a user opens a nested legacy profile URL in-app, rewrite to the monolith-known base path.
 *
 * @param {string} pathname
 * @param {string} search
 * @param {string} hash
 * @returns {{ pathname: string, search: string, hash: string } | null}
 */
export function legacyProfileNestedBrowserRedirectTarget(pathname, search, hash) {
  if (!pathname.startsWith(`${PROFILE_SETTINGS_BASE}/`)) {
    return null
  }
  const slug = pathname.slice(`${PROFILE_SETTINGS_BASE}/`.length).split('/').filter(Boolean)[0]
  if (slug == null || PROFILE_LEGACY_NESTED_SLUGS[slug] == null) {
    return null
  }
  return personalBrowserTabTarget(PROFILE_SETTINGS_BASE, slug, search, hash)
}

/**
 * @param {string} pathname
 * @param {string} search
 * @param {string} hash
 * @param {Set<string>} validTabSlugs
 * @returns {{ pathname: string, search: string, hash: string } | null}
 */
export function legacySecurityNestedBrowserRedirectTarget(pathname, search, hash, validTabSlugs) {
  if (!pathname.startsWith(SECURITY_LEGACY_NESTED_PREFIX)) {
    return null
  }
  const slug = pathname.slice(SECURITY_LEGACY_NESTED_PREFIX.length).split('/').filter(Boolean)[0]
  if (slug == null || !validTabSlugs.has(slug)) {
    return null
  }
  return personalBrowserTabTarget(PERSONAL_SECURITY_BASE, slug, search, hash)
}
